import ApimApiClient from "@/services/serviceClients/apimApiClient";

const WORLDPAY_GENERATE_TOKEN_API_PATH = "/WorldPay/GenerateToken";
const WORLDPAY_SAVE_TRANSACTION_API_PATH = "/WorldPay/SavePaymentTransaction";
const WORLDPAY_GET_PAYMENT_ACCOUNT_ID_API_PATH = "/WorldPay/GetPaymentAccountID";
const WORLDPAY_UPDATE_ORDER_ID_API_PATH = "/WorldPay/UpdateOrderId";

/**
 * WorldPay Payment Service
 * Handles WorldPay API integration for credit card payments
 */
class WorldPayService {
  static async generateToken(paymentDetails) {
    try {
      const formattedAmount = parseFloat(paymentDetails.amount || 0).toFixed(2);
      
      const payload = {
        customerCode: paymentDetails.customerCode || "",
        amount: formattedAmount,
        orderId: paymentDetails.orderId || "",
        currency: "USD",
        billingName: paymentDetails.billingName || "",
        billingEmail: paymentDetails.billingEmail || "",
        billingPhone: paymentDetails.billingPhone || "",
        billingAddress1: paymentDetails.billingAddress1 || "",
        billingAddress2: paymentDetails.billingAddress2 || "",
        billingCity: paymentDetails.billingCity || "",
        billingState: paymentDetails.billingState || "",
        billingZipcode: paymentDetails.billingZipcode || "",
      };

    
      const response = await ApimApiClient.post(WORLDPAY_GENERATE_TOKEN_API_PATH, payload);

      const tokenData = response.data || {};

      return {
        success: true,
        data: {
          ...tokenData,
          token: tokenData.Token ?? null,
          iframeUrl: tokenData.IframeUrl ?? null,
          validationCode: tokenData.ValidationCode ?? null,
          expires: tokenData.Expires ?? null,
          status: tokenData.Status ?? null
        },
        error: null
      };
    } catch (error) {
      let errorMessage = "Failed to generate WorldPay token";
      
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.error_description) {
          errorMessage = error.response.data.error_description;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  static async savePaymentTransaction(transactionDetails) {
    try {
      const payload = {
        transactionId: transactionDetails.transactionId || "",
        transactionType: transactionDetails.transactionType || "OrderCatalog",
        customerCode: transactionDetails.customerCode || "",
        orderId: transactionDetails.orderId || ""
      };

      const response = await ApimApiClient.post(WORLDPAY_SAVE_TRANSACTION_API_PATH, payload);

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description || 
                          error.response?.data?.message ||
                          error.message || 
                          "Failed to save payment transaction";
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  static async getPaymentAccountId(token) {
    try {
      const response = await ApimApiClient.get(`${WORLDPAY_GET_PAYMENT_ACCOUNT_ID_API_PATH}/${token}`);

      const serviceResponse = response.data || {};

      const payload = serviceResponse.data ?? null;

      const isStringPayload = typeof payload === "string";
      const paymentAccountId = isStringPayload
        ? payload || null
        : payload?.PaymentAccountID ?? null;

      const expressResponseCode = isStringPayload ? null : payload?.ExpressResponseCode ?? null;
      const expressResponseMessage = isStringPayload ? null : payload?.ExpressResponseMessage ?? null;
      const hostedPaymentStatus = isStringPayload ? null : payload?.HostedPaymentStatus ?? null;

      return {
        success: true,
        data: paymentAccountId || expressResponseMessage
          ? {
              paymentAccountId,
              expressResponseCode,
              expressResponseMessage,
              hostedPaymentStatus
            }
          : null,
        error: null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description ||
                          error.response?.data?.message ||
                          error.message ||
                          "Failed to get payment account ID";
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }

  static async updateOrderId(transactionSetupId, orderId) {
    try {
      const payload = {
        transactionSetupId: transactionSetupId || "",
        orderId: orderId != null ? String(orderId) : ""
      };

      const response = await ApimApiClient.post(WORLDPAY_UPDATE_ORDER_ID_API_PATH, payload);

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      const errorMessage = error.response?.data?.error_description ||
                          error.response?.data?.message ||
                          error.message ||
                          "Failed to update payment transaction order ID";
      return {
        success: false,
        data: null,
        error: errorMessage
      };
    }
  }
}

export default WorldPayService;
